import { useEffect, useRef } from 'react'

import { Query, useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { shouldPollWithStartupGrace } from '@/hooks/common/startupGrace'
import { integrationsQueryKeys } from '@/hooks/integrations/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { StatusResponse } from '@/types/Status'

type SyncableIntegrationProvider = 'salesforce'

type SyncStatusData = StatusResponse & {
  isSyncing: boolean
}

interface UseSyncIntegrationStatusArgs {
  orgId: UUID | ''
  provider: SyncableIntegrationProvider
  onSuccess?: () => void
}

export const fetchSyncStatus = async (
  orgId: UUID | '',
  provider: SyncableIntegrationProvider,
  accessToken: string,
): Promise<StatusResponse> => {
  if (!orgId) throw new Error('Organization ID is required')
  let url: string
  switch (provider) {
    case 'salesforce':
      url = `${API_URL}/api/v1/jobs/pull-salesforce/key/${orgId}`
      break
    default:
      const exhaustiveCheck: never = provider
      throw new Error(`Unknown provider: ${exhaustiveCheck}`)
  }
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
  return (await res.json()) as StatusResponse
}

const refetchInterval = <TQueryKey extends readonly unknown[]>(
  query: Query<StatusResponse, Error, StatusResponse, TQueryKey>,
) => {
  if (shouldPollWithStartupGrace(query.state.data?.status, false)) {
    return 10 * 1000
  }
  return 5 * 60 * 1000
}

const selectData = (data: StatusResponse): SyncStatusData => {
  return {
    ...data,
    isSyncing: data.status === 'pending' || data.status === 'running',
  }
}

export const useSyncIntegrationStatus = ({
  orgId,
  provider,
  onSuccess,
}: UseSyncIntegrationStatusArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  const wasRunningRef = useRef<boolean>(false)
  const queryKey = integrationsQueryKeys.orgSyncStatus({ orgId, provider })

  const query = useQuery({
    queryKey,
    queryFn: () => fetchSyncStatus(orgId, provider, accessToken),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    refetchInterval,
    select: selectData,
  })

  const data = query.data

  useEffect(() => {
    if (!data) {
      return
    }
    if (data.status === 'pending' || data.status === 'running') {
      wasRunningRef.current = true
      return
    }
    if (data.status === 'succeeded' && wasRunningRef.current) {
      onSuccess?.()
    }
    wasRunningRef.current = false
  }, [data, onSuccess])

  return {
    ...query,
    data,
  }
}
